import { forwardRef, useId, useContext, createElement } from 'react';
import { breakpoints, resets } from '@ui/css';
import { FloatingActionButton } from '@ui/kit';
import { describeArcPath } from '@utils/common';
import { useMatchBreakpoint, useActivityIndicator } from '@utils/react';
import { loader, progressCircle } from './button.style.js';
import { axisContext } from './context.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const FloatingActionButtonsContextMenuButton = /*#__PURE__*/forwardRef(function FabContextMenuButton({
  icon,
  label,
  tooltip,
  async,
  inContextMenu,
  ...props
}, ref) {
  const progressId = useId();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const isPending = typeof async?.progress === "number" && async.progress === 0;
  const isPerformingAsyncOperation = typeof async?.progress !== "undefined";
  const showLoader = useActivityIndicator(isPending, {
    delayMs: 250,
    durationMs: 375
  });
  const axis = useContext(axisContext);
  const buttonSizePx = isMobile ? 44 : 50;
  const Comp = inContextMenu ? "li" : "div";
  return /*#__PURE__*/jsxs(Comp, {
    children: [/*#__PURE__*/createElement(FloatingActionButton, {
      ...props,
      ref: ref,
      key: label,
      tooltip: {
        placement: axis === "vertical" ? "left" : "top",
        body: tooltip?.body || label,
        ariaType: tooltip?.ariaType || "label"
      },
      state: isPerformingAsyncOperation ? undefined : props.state,
      "aria-busy": async ? isPerformingAsyncOperation : undefined,
      "aria-describedby": async ? progressId : undefined
    }, /*#__PURE__*/jsx(FloatingActionButton.Icon, {
      children: icon
    }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
      contentKey: "loader",
      active: showLoader.isActive(),
      disableScaleDuringTransition: true,
      children: /*#__PURE__*/jsx("span", {
        className: loader
      })
    }), /*#__PURE__*/jsx(FloatingActionButton.Icon, {
      contentKey: "progress",
      active: isPerformingAsyncOperation && !showLoader.isActive(),
      disableScaleDuringTransition: true,
      children: /*#__PURE__*/jsx("svg", {
        xmlns: "http://www.w3.org/2000/svg",
        viewBox: `0 0 ${buttonSizePx} ${buttonSizePx}`,
        className: progressCircle,
        children: typeof async?.progress !== "undefined" && async.progress > 0 && /*#__PURE__*/jsx("path", {
          d: describeArcPath(buttonSizePx, 2, Math.min(359.99, Math.round(async?.progress * 360)))
        }, "progress")
      })
    })), async && /*#__PURE__*/jsx("span", {
      className: resets.screenReaderOnlyCls,
      id: progressId,
      "aria-label": `Status for ${async.type}`,
      role: "progressbar",
      "aria-valuemin": 0,
      "aria-valuemax": 100,
      "aria-valuenow": async?.progress ? Math.round(async.progress * 100) : 0
    })]
  });
});

export { FloatingActionButtonsContextMenuButton };
//# sourceMappingURL=button.js.map
